import { Box, Divider, Grid, Group, SimpleGrid, Text } from '@mantine/core';
import React, { useContext } from 'react';
import { ProjectCardFormProps, ProjectCardFormProvider } from './ProjectCardForm.model';
import { CustomDateInput } from '../CustomInputFields/CustomDateInput/CustomDateInput';
import { CustomTextInput } from '../CustomInputFields/CustomTextInput/CustomTextInput';
import { CustomTextArea } from '../CustomInputFields/CustomTextArea/CustomTextArea';
import { CustomSelect } from '../CustomInputFields/CustomSelect/CustomSelect';
import { FinancialPlanForm } from './ProjectCardSubForms/FinancialPlanForm';
import { CustomNumberInput } from '../CustomInputFields/CustomNumberInput/CustomNumberInput';
import classes from './ProjectCardForm.module.css';
import { CustomSubmitButton } from '../CustomButton/CustomSubmitButton';
import { EnumerationType } from '../../build/generated-sources/openapi';
import { NotificationType, showNotification } from '../../core/notifications/Notifications';
import { IndicatorsForm } from './ProjectCardSubForms/IndicatorsForm';
import { ContactEmailsForm } from './ProjectCardSubForms/ContactEmailsForm';
import { FollowUpsForm } from './ProjectCardSubForms/FollowUpsForm';
import { AuthContext } from '../../core/auth/AuthContextProvider';
import { CustomRadioGroup } from '../CustomInputFields/CustomRadioGroup/CustomRadioGroup';
import { ProjectApprovalsForm } from './ProjectCardSubForms/ProjectApprovalsForm';

export const ProjectCardForm = React.forwardRef((props: ProjectCardFormProps, ref) => {
  const {
    form,
    isUpdateProject,
    isLoading,
    combinedExpenses,
    updateCombinedExpenses,
    updateExpensesData,
    onFormSubmit,
    enumerations,
    followUps,
    createdBy,
    createdByLegacy,
    isLocked,
    isPrint
  } = props;
  const authContext = useContext(AuthContext);

  function canEditProject(): boolean {
    const { userProfile, hasRole } = authContext;

    // if DB is locked — no one can edit
    if (isLocked) return false;

    // if creating new project and DB is open — allowed
    if (!isUpdateProject) return true;

    // superuser, manager, sysadmin can edit everything
    if (hasRole(['superuser', 'manager', 'sysadmin'])) return true;

    // external_user and user can only edit their own projects
    return (
      hasRole(['external_user', 'user']) &&
      (createdBy?.keycloakId === userProfile?.keycloakId ||
        createdByLegacy === `${userProfile?.lastName} ${userProfile?.firstName}`)
    );
  }

  return (
    // @ts-ignore
    <div ref={ref}>
      <ProjectCardFormProvider form={form}>
        <form
          onSubmit={form.onSubmit(
            (values, event) => {
              if (onFormSubmit) {
                onFormSubmit(values, event);
              }
            },
            (validationErrors, _values, _event) => {
              showNotification(
                'Formulář je neplatný. Zkontrolujte prosím, zda máte vše správně vyplněno',
                'validationErrors',
                NotificationType.ERROR
              );
            }
          )}>
          <Box className={classes.box}>
            <Grid grow>
              <Grid.Col span={6}>
                <CustomTextArea
                  label="Název projektu"
                  inputProps={form.getInputProps('name')}
                  required={true}
                  minRows={7}
                  disabled={isLocked}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <CustomTextInput
                  label="Název nositele projektu"
                  inputProps={form.getInputProps('holder')}
                  required={true}
                  disabled={isLocked}
                />
                <CustomTextInput
                  label="IČ Nositele"
                  inputProps={form.getInputProps('holderIc')}
                  required={true}
                  disabled={isLocked}
                />
                <CustomSelect
                  label="Právní forma nositele projektu"
                  required={true}
                  disabled={isLocked}
                  data={enumerations
                    ?.filter((enumeration) => enumeration.type === EnumerationType.HolderLegalForm)
                    .map((entry) => {
                      return { value: entry.id + '', label: entry.translation };
                    })}
                  inputProps={form.getInputProps('holderLegalFormEnumerationId')}
                />
                <CustomRadioGroup
                  disabled={isLocked}
                  label="Prioritní projekt"
                  options={[
                    { label: 'ANO', value: true },
                    { label: 'NE', value: false }
                  ]}
                  value={form.values.priority}
                  onChange={(val) => form.setFieldValue('priority', val === 'true')}
                  inline
                />
                <CustomRadioGroup
                  disabled={isLocked}
                  label="Způsob financování"
                  options={[
                    { label: 'Ex-post', value: 'EX_POST' },
                    { label: 'Ex-ante', value: 'EX_ANTE' }
                  ]}
                  value={form.values.financingMethod}
                  onChange={(val) => form.setFieldValue('financingMethod', val)}
                  inline
                />
              </Grid.Col>
              {isUpdateProject && (
                <Grid.Col span={6}>
                  <CustomTextInput // automatically generated on BE
                    label="Evidenční číslo"
                    disabled={true}
                    inputProps={form.getInputProps('registrationNumber')}
                  />
                </Grid.Col>
              )}
              <Grid.Col span={6}>
                <CustomTextInput
                  disabled={isLocked}
                  label="Číslo akce"
                  inputProps={form.getInputProps('eventNumber')}
                />
              </Grid.Col>
            </Grid>
            <CustomTextInput
              label="Garant"
              disabled={isLocked}
              inputProps={form.getInputProps('guarantor')}
              required={true}
            />
            <CustomTextInput
              label="Kontaktní osoba/tel"
              disabled={isLocked}
              inputProps={form.getInputProps('contactPerson')}
              required={true}
            />
            <Divider mt={12} />
            <ContactEmailsForm form={form} disabled={isLocked || false} />
            <Divider mt={12} />
            <CustomTextInput
              label="Vedoucí projektu"
              inputProps={form.getInputProps('manager')}
              disabled={isLocked}
            />
            <div className="" style={{ height: '100%' }}>
              <CustomTextArea
                disabled={isLocked}
                label="Stručný popis projektu"
                inputProps={form.getInputProps('briefDescription')}
                required={true}
                isPrint={isPrint}
              />
            </div>
            <ProjectApprovalsForm form={form} disabled={isLocked} />
            <CustomNumberInput
              disabled={isLocked}
              label="Celkové náklady dle finančního příslibu"
              inputProps={form.getInputProps('totalCosts')}
            />
            <div className={classes.pageBreak} />
            <CustomTextInput
              disabled={isLocked}
              label="Výsledek/ výstup projektu"
              inputProps={form.getInputProps('result')}
            />
            <CustomTextInput
              disabled={isLocked}
              label="Kvantifikace výsledku - indikátory (indikátor, měrná jednotka, hodnota)"
              inputProps={form.getInputProps('resultQuantification')}
            />
            <CustomSelect
              disabled={isLocked}
              label="Typ projektu"
              required={true}
              data={enumerations
                ?.filter((enumeration) => enumeration.type === EnumerationType.ProjectType)
                .map((entry) => {
                  return { value: entry.id + '', label: entry.translation };
                })}
              inputProps={form.getInputProps('typeEnumerationId')}
            />
            <CustomTextArea
              disabled={isLocked}
              label="Spolupracující subjekt (IČ a název)"
              inputProps={form.getInputProps('partners')}
            />
            <CustomTextInput // can add more than 1
              disabled={isLocked}
              label="Místo realizace (kódy míst realizace a názvy míst realizace)"
              inputProps={form.getInputProps('realizationPlace')}
              required={true}
            />
            <FollowUpsForm form={form} followUps={followUps} disabled={isLocked} />
            <CustomSelect
              disabled={isLocked}
              label="Akční plány"
              clearable
              data={enumerations
                ?.filter((enumeration) => enumeration.type === EnumerationType.ActionPlan)
                .map((entry) => {
                  return { value: entry.id + '', label: entry.translation };
                })}
              inputProps={form.getInputProps('actionPlanEnumerationId')}
            />
            <CustomSelect
              disabled={isLocked}
              label="Stav rozpracovanosti navrhovaného projektu"
              data={enumerations
                ?.filter((enumeration) => enumeration.type === EnumerationType.DevelopmentState)
                .map((entry) => {
                  return { value: entry.id + '', label: entry.translation };
                })}
              inputProps={form.getInputProps('developmentStateEnumerationId')}
              required={true}
            />
            {/* [DAP-021] „Datum vydání právního aktu" vedle „Právní akt (rozhodnutí)" (na přání
                zákazníka side-by-side, ne pod sebou) — obojí v jednom dvousloupcovém gridu.
                Datum se zobrazuje jen když je Právní akt = ANO; při NE se skryje, ale hodnota
                zůstává ve form.values (nevynulujeme ji), takže po přepnutí zpět na ANO je zase
                vidět (a ukládá se dál). */}
            <SimpleGrid cols={2}>
              <CustomRadioGroup
                disabled={isLocked}
                label="Právní akt (rozhodnutí)"
                options={[
                  { label: 'ANO', value: true },
                  { label: 'NE', value: false }
                ]}
                value={form.values.legalAct}
                onChange={(val) => form.setFieldValue('legalAct', val === 'true')}
                inline
              />
              {form.values.legalAct && (
                <CustomDateInput
                  disabled={isLocked}
                  // [DAP-021] clearable = datum lze smazat (vynulovat na prázdno). Mantine
                  // DateInput bez `clearable` prázdný vstup neuloží jako null a na blur ho
                  // vrátí zpět na poslední hodnotu — proto to vypadalo, že „přidá výchozí datum".
                  clearable
                  label="Datum vydání právního aktu"
                  inputProps={form.getInputProps('legalActDate')}
                />
              )}
            </SimpleGrid>
            <div className={classes.pageBreak} />
            <Divider mt={12} />
            <IndicatorsForm form={form} disabled={isLocked} />
            <Divider mt={12} />
            <CustomSelect
              disabled={isLocked}
              label="OP nebo iniciativa"
              clearable
              data={enumerations
                ?.filter((enumeration) => enumeration.type === EnumerationType.Initiative)
                .map((entry) => {
                  return { value: entry.id + '', label: entry.translation };
                })}
              inputProps={form.getInputProps('initiativeEnumerationId')}
            />
            <SimpleGrid cols={2}>
              <CustomDateInput
                disabled={isLocked}
                label="Zahájení předprojektové přípravy"
                inputProps={form.getInputProps('preprojectPreparationStart')}
              />
            </SimpleGrid>
            <SimpleGrid cols={2}>
              <CustomDateInput
                disabled={isLocked}
                label="Zahájení realizace"
                inputProps={form.getInputProps('implementationStart')}
                required={true}
              />
              <CustomDateInput
                disabled={isLocked}
                label="Ukončení realizace"
                inputProps={form.getInputProps('implementationEnd')}
                required={true}
                minDate={form.values.implementationStart || new Date()}
              />
            </SimpleGrid>
            <CustomNumberInput
              disabled={isLocked}
              label="Délka fyzické realizace - v měsících"
              inputProps={form.getInputProps('implementationLength')}
              rightSection={''}
            />
            <SimpleGrid cols={2}>
              <CustomDateInput
                disabled={isLocked}
                label="Finanční ukončení projektu"
                inputProps={form.getInputProps('financialEnd')}
              />
            </SimpleGrid>
            <SimpleGrid cols={2}>
              <CustomDateInput
                disabled={isLocked}
                label="Zahájení doby udržitelnosti"
                inputProps={form.getInputProps('sustainabilityPeriodStart')}
              />
              <CustomDateInput
                disabled={isLocked}
                label="Ukončení doby udržitelnosti"
                inputProps={form.getInputProps('sustainabilityPeriodEnd')}
              />
            </SimpleGrid>
            <SimpleGrid cols={2}>
              <CustomSelect
                disabled={isLocked}
                label="Územní rozhodnutí"
                clearable
                data={enumerations
                  ?.filter((enumeration) => enumeration.type === EnumerationType.Decision)
                  .map((entry) => {
                    return { value: entry.id + '', label: entry.translation };
                  })}
                inputProps={form.getInputProps('territorialDecisionEnumerationId')}
              />
              <CustomSelect
                disabled={isLocked}
                label="Stavební povolení"
                clearable
                data={enumerations
                  ?.filter((enumeration) => enumeration.type === EnumerationType.Decision)
                  .map((entry) => {
                    return { value: entry.id + '', label: entry.translation };
                  })}
                inputProps={form.getInputProps('buildingPermitEnumerationId')}
              />
            </SimpleGrid>
            <CustomNumberInput
              disabled={isLocked}
              label="Celkové (předpokládané) náklady projektu (Kč, vč. DPH)"
              inputProps={form.getInputProps('totalEstimatedCosts')}
              required={true}
            />
            <CustomNumberInput
              disabled={isLocked}
              label="Náklady na přípravu projektu (Kč, vč. DPH)"
              inputProps={form.getInputProps('preparationCosts')}
            />
            <CustomNumberInput
              disabled={isLocked}
              label="Náklady na realizaci projektu (Kč, vč. DPH)"
              inputProps={form.getInputProps('implementationCosts')}
            />
            <CustomSelect
              disabled={isLocked}
              label="Předpokládaný hlavní zdroj financování"
              clearable
              data={enumerations
                ?.filter((enumeration) => enumeration.type === EnumerationType.FundingMainSource)
                .map((entry) => {
                  return { value: entry.id + '', label: entry.translation };
                })}
              inputProps={form.getInputProps('fundingMainSourceEnumerationId')}
            />
            <div className={classes.pageBreak} />
            <FinancialPlanForm
              form={form}
              combinedExpenses={combinedExpenses}
              updateCombinedExpenses={updateCombinedExpenses}
              updateExpensesData={updateExpensesData}
              disabled={isLocked}
            />
            <CustomTextInput
              label="Poznámky"
              inputProps={form.getInputProps('notes')}
              disabled={isLocked}
            />
            <CustomTextInput
              label="Klíčové slovo"
              inputProps={form.getInputProps('keyWord')}
              disabled={isLocked}
            />
            {isUpdateProject && (
              <>
                <CustomDateInput
                  label="Datum poslední změny"
                  disabled={true}
                  withTime={true}
                  inputProps={form.getInputProps('updated')}
                />
                <Group>
                  <CustomTextInput
                    label="Upravil nové / původní"
                    disabled={true}
                    inputProps={form.getInputProps('updatedBy')}
                  />
                  <Text mt={40}>/</Text>
                  <CustomTextInput
                    label={''}
                    disabled={true}
                    mt={40}
                    inputProps={form.getInputProps('createdBy')}
                  />
                </Group>
              </>
            )}
            <div className={classes.printHidden}>
              {canEditProject() && (
                <Group justify="flex-end" mt="md">
                  <CustomSubmitButton label={'Odeslat'} loading={isLoading} />
                </Group>
              )}
            </div>
          </Box>
        </form>
      </ProjectCardFormProvider>
    </div>
  );
});
